import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Package, Users, ShoppingBag, DollarSign, ChevronDown, ChevronUp,
  CheckCircle, Truck, Clock, AlertCircle, CreditCard, QrCode, LayoutDashboard, Archive, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

const STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  processing: 'bg-purple-100 text-purple-700',
  shipped: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const PAYMENT_STYLES = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
};

const getAdminKey = () => sessionStorage.getItem('adminKey') || '';
const adminHeaders = () => ({ 'X-Admin-Key': getAdminKey() });

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [verifyingRef, setVerifyingRef] = useState(null);

  useEffect(() => {
    if (!getAdminKey()) {
      navigate('/admin/login');
      return;
    }
    // Verify session
    axios.get(`${API}/admin/verify`, { headers: adminHeaders() })
      .catch(() => {
        sessionStorage.removeItem('adminKey');
        navigate('/admin/login');
      });
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [analyticsRes, ordersRes, customersRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/admin/orders`, { headers: adminHeaders() }),
        axios.get(`${API}/customers`),
      ]);
      setAnalytics(analyticsRes.data);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem('adminKey');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${newStatus}`);
      toast.success(`Order updated to "${newStatus}"`);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch {
      toast.error('Failed to update order status');
    }
  };

  const verifySnapScanPayment = async (paymentRef) => {
    setVerifyingRef(paymentRef);
    try {
      await axios.post(`${API}/payment/verify/${paymentRef}`, {}, { headers: adminHeaders() });
      toast.success('Payment verified successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify payment');
    } finally {
      setVerifyingRef(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminKey');
    navigate('/admin/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-perfect-peach" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-white">
      {/* Admin Top Bar */}
      <div className="bg-[#2D2420] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            <span className="font-playfair text-lg font-semibold text-perfect-peach">PPC Admin</span>
            <nav className="hidden sm:flex items-center gap-1">
              <Link
                to="/admin"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 text-white"
                data-testid="admin-nav-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <Link
                to="/admin/inventory"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                data-testid="admin-nav-inventory"
              >
                <Archive className="w-4 h-4" /> Inventory
              </Link>
              <Link
                to="/admin/subscribers"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                data-testid="admin-nav-subscribers"
              >
                <Mail className="w-4 h-4" /> Subscribers
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">View Store</Link>
            <button onClick={handleLogout} className="text-white/60 hover:text-white text-sm transition-colors" data-testid="admin-logout-button">
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Mobile admin nav */}
      <div className="sm:hidden bg-[#2D2420] border-t border-white/10 px-4 pb-2 flex gap-2 overflow-x-auto">
        <Link to="/admin" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white/10 text-white">
          <LayoutDashboard className="w-4 h-4" /> Dashboard
        </Link>
        <Link to="/admin/inventory" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/70">
          <Archive className="w-4 h-4" /> Inventory
        </Link>
        <Link to="/admin/subscribers" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/70">
          <Mail className="w-4 h-4" /> Subscribers
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-[#2D2420] mb-6" data-testid="admin-dashboard-heading">
          Dashboard
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Revenue', value: `R${analytics?.total_revenue?.toFixed(2) || '0.00'}`, icon: DollarSign, color: 'text-perfect-peach', bg: 'bg-perfect-peach/10', testId: 'total-revenue' },
            { label: 'Orders', value: analytics?.total_orders || 0, icon: ShoppingBag, color: 'text-royal-gold', bg: 'bg-royal-gold/10', testId: 'total-orders' },
            { label: 'Customers', value: analytics?.total_customers || 0, icon: Users, color: 'text-african-earth', bg: 'bg-african-earth/10', testId: 'total-customers' },
            { label: 'Products', value: analytics?.total_products || 0, icon: Package, color: 'text-perfect-peach', bg: 'bg-perfect-peach/10', testId: 'total-products' },
          ].map(stat => (
            <div key={stat.label} className="bg-white rounded-2xl p-4 sm:p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${stat.bg} flex items-center justify-center flex-shrink-0`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-african-earth text-xs sm:text-sm truncate">{stat.label}</p>
                  <p className="text-lg sm:text-xl font-bold text-[#2D2420] truncate" data-testid={stat.testId}>{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-2xl shadow-soft mb-8 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-[#E0D5D0]">
            <h2 className="font-playfair text-xl sm:text-2xl font-semibold text-[#2D2420]">
              Orders ({orders.length})
            </h2>
          </div>

          {orders.length === 0 ? (
            <div className="p-8 text-center text-african-earth">No orders yet</div>
          ) : (
            <div className="divide-y divide-[#F0EAE6]">
              {orders.map((order) => (
                <div key={order.id} data-testid={`order-row-${order.id}`}>
                  {/* Order Row */}
                  <div
                    className="flex items-center gap-3 px-4 sm:px-6 py-4 cursor-pointer hover:bg-bone-white/50 transition-colors"
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-mono text-[#2D2420] font-medium">#{order.id.slice(0, 8)}</span>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-700'}`}>
                          {order.status}
                        </span>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${PAYMENT_STYLES[order.payment_status] || 'bg-gray-100 text-gray-700'}`}>
                          {order.payment_status === 'paid' ? 'Paid' : order.payment_status}
                        </span>
                        {order.payment_method === 'snapscan' && (
                          <QrCode className="w-3.5 h-3.5 text-african-earth/50" />
                        )}
                        {order.payment_method === 'yoco' && (
                          <CreditCard className="w-3.5 h-3.5 text-african-earth/50" />
                        )}
                      </div>
                      <p className="text-xs text-african-earth mt-0.5 truncate">
                        {order.customer_name} · {formatDate(order.created_at)}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-perfect-peach whitespace-nowrap">R{order.total_amount?.toFixed(2)}</span>
                    {expandedOrder === order.id ? <ChevronUp className="w-4 h-4 text-african-earth/40" /> : <ChevronDown className="w-4 h-4 text-african-earth/40" />}
                  </div>

                  {/* Expanded Order Detail */}
                  {expandedOrder === order.id && (
                    <div className="px-4 sm:px-6 pb-5 bg-bone-white/30 border-t border-[#F0EAE6]">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                        {/* Customer & Shipping */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-african-earth/60 font-medium uppercase tracking-wide mb-1">Customer</p>
                            <p className="text-sm text-[#2D2420]">{order.customer_name}</p>
                            <p className="text-sm text-african-earth">{order.customer_email}</p>
                          </div>
                          {order.shipping_address && (
                            <div>
                              <p className="text-xs text-african-earth/60 font-medium uppercase tracking-wide mb-1">Shipping Address</p>
                              <p className="text-sm text-[#2D2420] leading-relaxed">
                                {order.shipping_address.address}<br />
                                {order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.postal_code}<br />
                                {order.shipping_address.phone}
                              </p>
                              <p className="text-xs text-african-earth mt-1">
                                {order.shipping_option === 'local' ? 'Local Delivery (West Rand)' : 'Nationwide Courier'}
                                {order.shipping_cost > 0 ? ` · R${order.shipping_cost?.toFixed(2)}` : ' · FREE'}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Items & Payment */}
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-african-earth/60 font-medium uppercase tracking-wide mb-1">Items</p>
                            <div className="space-y-1">
                              {order.items?.map((item, i) => (
                                <div key={i} className="flex justify-between text-sm">
                                  <span className="text-african-earth">{item.quantity}x (ID: {item.product_id?.slice(0, 6)}...)</span>
                                  <span className="text-[#2D2420] font-medium">R{(item.price * item.quantity).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-african-earth/60 font-medium uppercase tracking-wide mb-1">Payment</p>
                            <p className="text-sm text-[#2D2420]">
                              {order.payment_method === 'yoco' ? 'Card (Yoco)' : order.payment_method === 'snapscan' ? 'SnapScan' : order.payment_method || 'Unknown'}
                              {order.payment_ref && <span className="text-african-earth ml-1">· Ref: {order.payment_ref}</span>}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-[#E0D5D0]">
                        {/* Status Dropdown */}
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-african-earth font-medium">Status:</label>
                          <select
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            className="border border-[#E0D5D0] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach bg-white"
                            data-testid={`order-status-select-${order.id}`}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                            ))}
                          </select>
                        </div>

                        {/* SnapScan Verify Button */}
                        {order.payment_method === 'snapscan' && order.payment_status !== 'paid' && order.payment_ref && (
                          <button
                            onClick={(e) => { e.stopPropagation(); verifySnapScanPayment(order.payment_ref); }}
                            disabled={verifyingRef === order.payment_ref}
                            className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                            data-testid={`verify-payment-${order.id}`}
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            {verifyingRef === order.payment_ref ? 'Verifying...' : 'Verify SnapScan Payment'}
                          </button>
                        )}

                        {order.payment_status === 'paid' && (
                          <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                            <CheckCircle className="w-3.5 h-3.5" /> Payment confirmed
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Customers */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-soft">
          <h2 className="font-playfair text-xl sm:text-2xl font-semibold text-[#2D2420] mb-4">
            Customers ({customers.length})
          </h2>
          {customers.length === 0 ? (
            <p className="text-african-earth text-sm">No customers yet</p>
          ) : (
            <div className="space-y-3">
              {customers.slice(0, 10).map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-3 sm:p-4 bg-bone-white rounded-xl"
                  data-testid={`customer-${customer.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-[#2D2420] text-sm truncate">{customer.name}</p>
                    <p className="text-xs text-african-earth truncate">{customer.email}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className="font-medium text-perfect-peach text-sm">R{customer.total_spent?.toFixed(2)}</p>
                    <p className="text-xs text-african-earth">{customer.orders_count} order{customer.orders_count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
